import { Component, computed, inject, input, output } from '@angular/core';
import { FieldContext } from '../../models/field-context';
import { FieldIntegrationStatus, IntegrationStatus } from '../../models/integration-status';
import { FieldDashboardDataService } from '../field-dashboard-data.service';
import { DATE_TIME, DAY, SAST, fieldHeadline, formatKm, pillClasses, reasonClasses } from './integration-status.util';

export interface TapRow {
  tap: string;
  /** Manual or Automatic, from the field context; empty until it has loaded. */
  how: string;
  /** Stored system name of the tap's newest run. Null: no runs yet. */
  system: string | null;
  lastRunAt: string | null;
  /** That system belongs to another client than the field's. */
  otherTenant: boolean;
  /** That client's name, when known. */
  systemClient: string | null;
}

/**
 * "This field" in the integration drawer: FMS, iGator and weather cards for the open field. Link
 * details come from the status call; Manual or Automatic from the field context the page already
 * holds. The edit actions open Field Analysis's own link editor.
 */
@Component({
  selector: 'app-integration-this-field',
  templateUrl: './integration-this-field.component.html',
  standalone: false
})
export class IntegrationThisFieldComponent {
  readonly status = input<FieldIntegrationStatus | null>(null);
  /** The field context once loaded for this field, else null (see loadedFieldContext). */
  readonly context = input<FieldContext | null>(null);
  /** The field context call failed, as opposed to still loading. */
  readonly linksFailed = input(false);
  readonly loading = input(false);
  readonly failed = input(false);
  readonly retry = output<void>();

  private readonly dataService = inject(FieldDashboardDataService);

  readonly DATE_TIME = DATE_TIME;
  readonly DAY = DAY;
  readonly SAST = SAST;
  readonly pillClasses = pillClasses;
  readonly reasonClasses = reasonClasses;

  readonly headline = computed(() => {
    const s = this.status();
    return s ? fieldHeadline([
      { name: 'FMS', status: s.fms.status },
      { name: 'iGator', status: s.igator.status },
      { name: 'Weather', status: s.weather.status }
    ]) : '';
  });
  readonly overall = computed<IntegrationStatus>(() => this.status()?.overall ?? 'NOT_SET_UP');

  /** "Block C3 · Trawal Fresh Fruit · Automatic", or null when there is no block. */
  readonly fmsLink = computed(() => {
    const fms = this.status()?.fms;
    if (!fms?.blockNo) {
      return null;
    }
    const ctx = this.context()?.fms;
    const how = ctx?.linked ? (ctx.manual ? 'Manual' : 'Automatic') : '';
    return ['Block ' + fms.blockNo, fms.sourceLabel || fms.source, how].filter(Boolean).join(' · ');
  });

  readonly taps = computed<TapRow[]>(() => {
    const details = this.context()?.igator?.tapDetails ?? [];
    return (this.status()?.igator.taps ?? []).map(t => {
      const detail = details.find(d => d.tap === t.tap);
      return {
        tap: t.tap,
        how: detail ? (detail.source === 'MANUAL' ? 'Manual' : 'Automatic') : '',
        system: t.system,
        lastRunAt: t.lastRunAt,
        otherTenant: t.otherTenant,
        systemClient: t.otherTenant ? (t.systemTenant?.name || null) : null
      };
    });
  });

  /** One warning line for taps that last ran under another client's iGator system. */
  readonly crossTenantTaps = computed(() => {
    const crossing = this.taps().filter(t => t.otherTenant);
    if (crossing.length === 0) {
      return null;
    }
    const owners = Array.from(new Set(crossing.map(t => t.systemClient).filter(Boolean)));
    const taps = crossing.length === 1 ? '1 tap last ran' : `${crossing.length} taps last ran`;
    return `${taps} under another client's iGator system${owners.length ? ` (${owners.join(', ')})` : ''}. Check the taps.`;
  });

  readonly stationLine = computed(() => {
    const s = this.status();
    if (!s) {
      return '';
    }
    const station = s.weather.station;
    if (!station) {
      return `None linked to ${s.tenant?.name || "this field's client"}`;
    }
    return station.customName ? `${station.customName} · ${station.code}` : station.code;
  });

  /** "1,4 km from this field", with the limit when it is farther; null without a station. */
  readonly distanceLine = computed(() => {
    const weather = this.status()?.weather;
    if (!weather?.station) {
      return null;
    }
    const km = formatKm(weather.distanceKm);
    if (km === null) {
      return 'Distance unknown';
    }
    const far = weather.distanceKm > weather.checkKm;
    return `${km} km from this field${far ? `, more than ${formatKm(weather.checkKm)} km` : ''}`;
  });

  /**
   * Why the edit actions are off, or null when they work. The link editor starts from the field
   * context and saves the full tap list, so they wait for it, as Field Analysis's own link rows do.
   * The buttons stay focusable (aria-disabled), so focus coming back from the editor still lands.
   */
  readonly editBlocked = computed(() => {
    if (this.context()) {
      return null;
    }
    return this.linksFailed() ? 'Links could not be loaded, so they cannot be edited here.' : 'Links are still loading.';
  });

  editLink(tab: 'fms' | 'igator'): void {
    if (this.editBlocked()) {
      return;
    }
    this.dataService.requestLinkEditor(tab);
  }
}
